import { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import { Tag } from '@/components/Tag';
import type {
  AgentApiCandidateImportRequestsShow,
  SharedCsvFieldMapping,
  SharedCsvDefaultMapping,
} from '@/types/serializers';
import { useUpdateCandidateImportRequest } from '../api/useUpdateCandidateImportRequest';
import { useCreateCsvMappingTemplate } from '../api/useCreateCsvMappingTemplate';
import { MdKeyboardArrowLeft } from 'react-icons/md';
import { getFieldTypeIcon, getFieldTypeLabel } from '../../customize/utils/fieldTypeHelpers';
import { showErrorToast } from '../../job_change_processes/lib/toast';

interface Props {
  importRequest: AgentApiCandidateImportRequestsShow;
  encryptId: string;
  fieldMappings: Record<number, number[]>;
  defaultMappings: Record<number, string>;
  selectedOrganizationUserId: number | null;
  nameCsvIndices: number[];
  nameDefaultValue: string;
  initialTitle?: string;
  onSaveSuccess: () => void;
  onBack: () => void;
}

export function CandidateImportPresetSaveView({
  importRequest,
  encryptId,
  fieldMappings,
  defaultMappings,
  selectedOrganizationUserId,
  nameCsvIndices,
  nameDefaultValue,
  initialTitle = '',
  onSaveSuccess,
  onBack,
}: Props) {
  const updateMutation = useUpdateCandidateImportRequest();
  const createTemplateMutation = useCreateCsvMappingTemplate();
  const [presetTitle, setPresetTitle] = useState(initialTitle);

  // コンポーネントマウント時にページトップにスクロール
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSave = async () => {
    if (!presetTitle.trim()) {
      showErrorToast('設定タイトルを入力してください');
      return;
    }

    try {
      const field_mappings = Object.entries(fieldMappings).map(([customFieldId, csvIndex]) => ({
        custom_field_id: Number(customFieldId),
        csv_index: csvIndex,
      }));

      // 氏名のマッピング
      const name_mappings: Array<{ csv_index: number; csv_header: string; default_value: string }> =
        [
          {
            csv_index: nameCsvIndices[0] !== undefined ? nameCsvIndices[0] : 0,
            csv_header:
              importRequest.header_array?.[nameCsvIndices[0]] ||
              importRequest.header_array?.[0] ||
              '氏名',
            default_value: nameDefaultValue,
          },
        ];

      const default_mappings = Object.entries(defaultMappings)
        .filter(([, value]) => value)
        .map(([customFieldId, value]) => ({
          custom_field_id: Number(customFieldId),
          default_value: value,
        }));

      // マッピング更新
      await updateMutation.mutateAsync({
        encryptId,
        field_mappings,
        name_mappings,
        default_mappings,
        organization_user_id: selectedOrganizationUserId,
      });

      // テンプレート作成
      await createTemplateMutation.mutateAsync({
        title: presetTitle,
        import_request_id: encryptId,
      });

      onSaveSuccess();
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : '保存に失敗しました。');
    }
  };

  // importRequestのマッピングとpropsのマッピングをマージ
  const mergedFieldMappings = useMemo(() => {
    const result: Record<number, number[]> = {};

    // importRequestのfield_mappingを初期値として設定
    if (importRequest.field_mapping && Array.isArray(importRequest.field_mapping)) {
      importRequest.field_mapping.forEach((mapping) => {
        const fieldMapping = mapping as SharedCsvFieldMapping;
        if (fieldMapping.custom_field_id && fieldMapping.csv_index) {
          result[fieldMapping.custom_field_id] = fieldMapping.csv_index;
        }
      });
    }

    // propsのfieldMappingsでオーバーライド
    Object.entries(fieldMappings).forEach(([customFieldId, csvIndex]) => {
      result[Number(customFieldId)] = csvIndex;
    });

    return result;
  }, [importRequest.field_mapping, fieldMappings]);

  const mergedDefaultMappings = useMemo(() => {
    const result: Record<number, string> = {};

    // importRequestのdefault_mappingを初期値として設定
    if (importRequest.default_mapping && Array.isArray(importRequest.default_mapping)) {
      importRequest.default_mapping.forEach((mapping) => {
        const defaultMapping = mapping as SharedCsvDefaultMapping;
        if (defaultMapping.custom_field_id && defaultMapping.default_value) {
          result[defaultMapping.custom_field_id] = defaultMapping.default_value;
        }
      });
    }

    // propsのdefaultMappingsでオーバーライド
    Object.entries(defaultMappings).forEach(([customFieldId, value]) => {
      result[Number(customFieldId)] = value;
    });

    return result;
  }, [importRequest.default_mapping, defaultMappings]);

  return (
    <>
      {/* メインコンテンツ */}
      <div className="px-lg pt-sm pb-20">
        <div className="rounded-sm bg-white p-lg shadow-layer1">
          <div className="space-y-lg">
            {/* ヘッダー */}
            <h2 className="border-b border-border text-heading-md">取り込みプリセットの保存</h2>

            {/* 設定タイトル */}
            <div className="flex flex-col gap-2 border-b border-border">
              <label className="text-heading-xxs">設定タイトル</label>
              <Input
                type="text"
                value={presetTitle}
                onChange={(e) => setPresetTitle(e.target.value)}
                placeholder="テキスト"
                className="w-[236px]"
                required
              />
            </div>

            {/* データの割り当て */}
            <div className="flex flex-col gap-2">
              <h3 className="text-heading-xxs">データの割り当て</h3>

              <div className="flex flex-col">
                {/* ヘッダー行 */}
                <div className="flex h-[22px] items-end gap-md bg-gray px-md">
                  <div className="w-[132px]">
                    <p className="text-heading-3xs text-sub">エージェントハブ</p>
                  </div>
                  <div className="invisible w-[160px]">
                    <p className="text-heading-xxs text-sub">割り当て先</p>
                  </div>
                  <div className="invisible w-6" />
                  <div className="flex-1">
                    <p className="text-heading-3xs text-sub">CSV</p>
                  </div>
                </div>

                {/* 情報タイプヘッダー */}
                <div className="flex h-xxl items-center gap-md border-b border-solid border-border bg-gray px-md">
                  <div className="w-[132px]">
                    <p className="text-heading-xxs text-sub">情報タイプ</p>
                  </div>
                  <div className="w-[160px]">
                    <p className="text-heading-xxs text-sub">割り当て先</p>
                  </div>
                  <div className="invisible w-6" />
                  <div className="flex-1">
                    <p className="text-heading-xxs text-sub">CSV項目/固定値を入力</p>
                  </div>
                </div>

                {/* 氏名行（必須） */}
                <div className="flex items-center gap-4 border-b border-border p-4">
                  {/* 情報タイプ */}
                  <div className="flex w-[132px] flex-col gap-1">
                    <div className="flex h-6 w-fit items-center gap-xxs rounded-full bg-tag-gray px-sm">
                      {getFieldTypeIcon('short_text')}
                      <span className="text-xs text-tag-gray">
                        {getFieldTypeLabel('short_text')}
                      </span>
                    </div>
                  </div>

                  {/* 割り当て先 */}
                  <div className="w-[160px]">
                    <p className="text-heading-sm">氏名</p>
                  </div>

                  <div className="w-6">
                    <MdKeyboardArrowLeft size={24} className="text-sub" />
                  </div>

                  {/* CSV項目または固定値 */}
                  <div className="flex flex-1 items-center gap-xs">
                    {nameDefaultValue ? (
                      <>
                        <p className="text-heading-xxs font-normal text-sub">固定値</p>
                        <Input
                          type="text"
                          value={nameDefaultValue}
                          disabled
                          className="w-[236px]"
                        />
                      </>
                    ) : nameCsvIndices.length > 0 ? (
                      <>
                        <p className="text-heading-xxs font-normal text-sub">CSV項目</p>
                        <Tag color="default" size="lg">
                          {importRequest.header_array?.[nameCsvIndices[0]]}
                        </Tag>
                      </>
                    ) : null}
                  </div>
                </div>

                {/* マッピング行（選択済みの状態） */}
                {importRequest.custom_fields
                  ?.filter(
                    (field) =>
                      mergedFieldMappings[field.id]?.length > 0 ||
                      mergedDefaultMappings[field.id]?.length > 0
                  )
                  .map((field) => {
                    const csvIndices = mergedFieldMappings[field.id] || [];
                    const defaultValue = mergedDefaultMappings[field.id];

                    return (
                      <div
                        key={field.id}
                        className="flex items-center gap-4 border-b border-border p-4"
                      >
                        {/* 情報タイプ */}
                        <div className="flex w-[132px] flex-col gap-1">
                          <div className="flex h-6 w-fit items-center gap-1 rounded-full bg-gray-100 px-2">
                            {getFieldTypeIcon(field.field_type)}
                            <span className="text-xs text-gray-700">
                              {getFieldTypeLabel(field.field_type)}
                            </span>
                          </div>
                        </div>

                        {/* 割り当て先 */}
                        <div className="w-[160px]">
                          <p className="text-heading-sm">{field.name}</p>
                        </div>

                        <div className="w-6">
                          <MdKeyboardArrowLeft size={24} className="text-sub" />
                        </div>

                        {/* CSV項目または固定値 */}
                        <div className="flex flex-1 items-center gap-xs">
                          {defaultValue ? (
                            <>
                              <p className="text-heading-xxs font-normal text-sub">固定値</p>
                              <Input
                                type="text"
                                value={defaultValue}
                                disabled
                                className="w-[236px]"
                              />
                            </>
                          ) : csvIndices.length > 0 ? (
                            <>
                              <p className="text-heading-xxs font-normal text-sub">CSV項目</p>
                              <Tag color="default" size="lg">
                                {importRequest.header_array?.[csvIndices[0]]}
                              </Tag>
                            </>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 下部固定アクションバー */}
      <div className="fixed inset-x-0 bottom-0 z-10 flex justify-end gap-2 border-t border-border bg-white p-[14px] shadow-layer3">
        <Button color="secondary" size="lg" type="button" onClick={onBack}>
          もどる
        </Button>
        <Button
          color="primary"
          size="lg"
          type="button"
          onClick={handleSave}
          disabled={
            updateMutation.isPending || createTemplateMutation.isPending || !presetTitle.trim()
          }
        >
          {updateMutation.isPending || createTemplateMutation.isPending
            ? '処理中...'
            : '保存して取り込みを開始'}
        </Button>
      </div>
    </>
  );
}
